import type { Item, ItemId, ScenarioId } from '@finsim/model';
import { useUnit } from 'effector-react';
import { AlertTriangle, Copy, Info, Trash2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { $project, ItemIcon, usageCount } from '../../../entities/project';
import { $result } from '../../../entities/simulation';
import { currencySymbol, formatMoney, formatPercent } from '../../../shared/lib/format';
import { useLocale } from '../../../shared/lib/i18n/use-locale';
import { Button, CommitField, EditOnly } from '../../../shared/ui';
import { duplicateRequested, removeRequested } from '../../item-actions';
import { itemEdited } from '../model';
import { AssetForm } from './asset-form';
import type { FormContext } from './context';
import { FlowForm } from './flow-form';
import { FormulaForm } from './formula-form';
import { InvestmentSection } from './investment-section';
import { ItemInsights } from './item-insights';
import { LoanForm } from './loan-form';

export function ItemInspector({ itemId, scenarioId }: { itemId: ItemId; scenarioId: ScenarioId }) {
  const { t } = useTranslation();
  const locale = useLocale();
  const [project, result, edit, duplicate, remove] = useUnit([
    $project,
    $result,
    itemEdited,
    duplicateRequested,
    removeRequested,
  ]);
  const item = project?.items[itemId];
  if (!project || !item) return null;

  const ctx: FormContext = {
    project,
    scenarioId,
    symbol: currencySymbol(project.assumptions.currency, locale),
    money: (v: number) => formatMoney(v, locale, { currency: project.assumptions.currency }),
    percent: (v: number) => formatPercent(v, locale, 2),
    commit: (next: Item, field: string) => {
      edit({ item: next, field });
    },
  };

  const usedIn = project.scenarios.filter((s) => s.itemIds.includes(itemId));
  const shared = (usageCount(project).get(itemId) ?? 0) > 1;
  const diagnostics = (result?.scenarios[scenarioId]?.diagnostics ?? []).filter(
    (d) => d.itemId === itemId,
  );

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="min-h-0 flex-1 overflow-y-auto">
        <header className="space-y-2 px-4 pt-3 pb-3">
          <div className="flex items-center gap-2">
            <span className="flex size-7 shrink-0 items-center justify-center rounded-md border border-border-subtle bg-bg-2 text-text-2">
              <ItemIcon item={item} className="size-4" />
            </span>
            <CommitField
              aria-label={t('fields.name')}
              className="min-w-0 flex-1 [&_input]:h-8 [&_input]:border-transparent [&_input]:bg-transparent [&_input]:px-1 [&_input]:text-base [&_input]:font-medium [&_input]:hover:border-border-strong"
              value={item.name}
              format={(v) => v}
              parse={(text) =>
                text.trim()
                  ? { ok: true, value: text.trim().slice(0, 120) }
                  : { ok: false, error: t('fields.required') }
              }
              onCommit={(name) => {
                edit({ item: { ...item, name }, field: 'name' });
              }}
            />
          </div>
          <div className="flex flex-wrap items-center gap-1.5 text-xs text-text-3">
            <span>
              {shared ? t('itemActions.sharedIn', { count: usedIn.length }) : t('inspector.usedIn')}
            </span>
            {usedIn.map((s) => (
              <span
                key={s.id}
                className="inline-flex items-center gap-1 rounded-sm bg-bg-3 px-1.5 py-0.5 text-text-2"
              >
                <span
                  aria-hidden
                  className="size-1.5 rounded-full"
                  style={{ background: `var(--chart-series-${String(s.color + 1)})` }}
                />
                {s.name}
              </span>
            ))}
          </div>
          {diagnostics.length > 0 && (
            <ul className="space-y-1">
              {diagnostics.map((d, i) => (
                <li
                  key={`${d.code}-${String(i)}`}
                  className="flex items-start gap-1.5 rounded-md bg-bg-2 px-2 py-1.5 text-xs text-text-2"
                >
                  {d.severity === 'info' ? (
                    <Info className="mt-px size-3.5 shrink-0 text-text-3" aria-hidden />
                  ) : (
                    <AlertTriangle
                      className={`mt-px size-3.5 shrink-0 ${d.severity === 'error' ? 'text-negative' : 'text-status-warning'}`}
                      aria-hidden
                    />
                  )}
                  {t(`diagnostics.${d.code}`)}
                </li>
              ))}
            </ul>
          )}
        </header>

        <ItemInsights item={item} scenarioId={scenarioId} currency={project.assumptions.currency} />
        {item.type === 'flow' && <FlowForm item={item} ctx={ctx} />}
        {item.type === 'formula' && <FormulaForm item={item} ctx={ctx} />}
        {item.type === 'asset' && <AssetForm item={item} ctx={ctx} />}
        {item.type === 'loan' && <LoanForm item={item} ctx={ctx} />}
        <InvestmentSection item={item} ctx={ctx} />
      </div>

      <EditOnly>
        <footer className="flex shrink-0 items-center justify-end gap-1.5 border-t border-border-subtle px-3 py-2">
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Copy />}
            onClick={() => {
              duplicate({
                itemId,
                scenarioId,
                name: t('itemActions.copyName', { name: item.name }),
              });
            }}
          >
            {t('itemActions.duplicate')}
          </Button>
          <Button
            size="sm"
            variant="ghost"
            leadingIcon={<Trash2 />}
            className="hover:text-negative"
            onClick={() => {
              remove({ itemId, scenarioId });
            }}
          >
            {shared ? t('itemActions.removeFromVariant') : t('itemActions.delete')}
          </Button>
        </footer>
      </EditOnly>
    </div>
  );
}
